import classNames from 'classnames'
import { Icon } from 'components/common/Icon'
import routes from 'constants/routes'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'
import { PropsWithClassName } from 'types'
import { Artwork, Edition } from 'utils/data'
import { getArtworkUrlFromRoot } from 'utils/urls'

interface Props extends PropsWithClassName {
  artwork: Artwork
  currentEdition: Edition
}

export const ShippingSteps = ({
  artwork: { id, name, artists },
  currentEdition,
  className,
}: Props) => {
  const { t } = useTranslation('shipping_and_billing_form')

  const router = useRouter()
  const { asPath, query } = router
  const isBid = router.query.tradeType === 'bid'

  const steps = [
    {
      path: routes.bidOrBuy.shippingAndBilling,
      label: 'shipping',
    },
    {
      path: routes.bidOrBuy.reviewBid,
      label: isBid ? 'review_billing' : 'review_and_pay',
    },
  ]

  const currentStepIndex = steps.findIndex(step => asPath.startsWith(step.path))

  const backHandler = () => {
    switch (true) {
      case asPath.startsWith(routes.bidOrBuy.reviewBid): {
        router.push({
          query,
          pathname:
            getArtworkUrlFromRoot(
              routes.bidOrBuy.shippingAndBilling,
              artists,
              name,
              id
            ) + `/${currentEdition.name}`,
        })
        break
      }
    }
  }

  const onStepClick = (index: number) => {
    if (index >= currentStepIndex) return

    backHandler()
  }

  return (
    <div className={classNames(className, 'grid w-full grid-cols-2 gap-x-2')}>
      {steps.map(({ path, label }, i) => {
        const isPassed = i < currentStepIndex
        const isActive = i === currentStepIndex

        return (
          <button
            type='button'
            key={path}
            className='flex flex-col space-y-1'
            onClick={() => onStepClick(i)}
            disabled={!isPassed}
          >
            <div
              className={classNames(
                'h-0.5 w-full rounded-full bg-cool-400',
                isActive && 'bg-secondary-default',
                isPassed && 'bg-cool-900'
              )}
            />

            <div className='flex items-center space-x-2'>
              {!isPassed && (
                <div
                  className={classNames(
                    'flex h-4 w-4 items-center justify-center rounded-full border border-cool-400 bg-cool-50',
                    isActive && 'border-transparent bg-secondary-default'
                  )}
                >
                  {isActive && (
                    <div className='h-1 w-1 rounded-full bg-cool-50'></div>
                  )}
                </div>
              )}

              {isPassed && <Icon name='filled-success' filled size={16} />}

              <span
                className={classNames(
                  'text-sm text-cool-600',
                  isActive && 'text-secondary-default font-medium',
                  isPassed && 'text-cool-900'
                )}
              >
                {i + 1}. {t(label)}
              </span>
            </div>
          </button>
        )
      })}
    </div>
  )
}
